/**
 * Chart — single ECharts wrapper covering Ando's 7 chart types.
 *
 * Public API:
 *   <Chart type="bar" data={rows} columns={cols} viz={vizSpec} title="..." />
 *
 * Architecture (PR-2 of the V2 dashboard parity plan):
 *   - Purely presentational, fully controlled by props
 *   - ECharts loaded lazily via `React.lazy()` — never lands in the main
 *     bundle (verified via `pnpm build` + manualChunks split)
 *   - Option-building delegated to the pure `buildOption()` helper in
 *     `chartTypes.ts` so it's unit-testable without React or jsdom
 *   - Table type uses a real HTML `<table>` (no canvas) so it's
 *     keyboard-navigable + screen-reader-friendly out of the box and
 *     wire-shape-compatible with PR-A2's future `<DataPagedTable>`
 *   - Skip-link "Visualizza come tabella" toggles a table view of the same
 *     data for screen readers / users who prefer tabular consumption
 *
 * Reused by dashboard chart cards (PR-1/2), chat structured-data cards
 * (PR-A2), and the inline enterprise panel (PR-A3) — that's why this
 * lives in `@ando/ui` rather than inside `apps/client`.
 */

import { msg } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { ChevronDown, ChevronUp, Table as TableIcon } from "lucide-react";
import * as React from "react";
import { DataPagedTable, type DataColumnMeta } from "../ando/DataPagedTable.js";
import { useReducedMotion } from "../hooks/use-reduced-motion.js";
import { cn } from "../lib/cn.js";
import { useI18n } from "../lib/i18n.js";
import { Skeleton } from "../primitives/skeleton.js";
import {
  type ChartType,
  type ChartVisualization,
  buildOption,
  resolveChartDimension,
  resolveChartColumnMeta,
  resolveChartTableColumns,
} from "./chartTypes.js";
import { ChartImplLazy } from "./lazy.js";

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export interface ChartClickPayload {
  /** Actual category column selected by the renderer. */
  readonly field?: string;
  /** The category/x-value of the clicked element. */
  readonly name: string;
  /** Series name (Y-field label when multi-series). */
  readonly seriesName?: string;
  /** Numeric value of the clicked point. */
  readonly value: number;
}

export interface ChartProps {
  readonly type: ChartType;
  readonly data: ReadonlyArray<Record<string, unknown>>;
  readonly columns: ReadonlyArray<string>;
  readonly viz?: ChartVisualization;
  readonly title?: string;
  readonly className?: string;
  /**
   * Fires when the user clicks a data point (cross-filter wiring, PR-6).
   * Pure callback — keep the type minimal but extensible.
   */
  readonly onElementClick?: (payload: ChartClickPayload) => void;
  /**
   * Optional aria-label. Defaults to "Grafico [type]: [title]".
   */
  readonly ariaLabel?: string;
  /**
   * When the parent renders its own loading state, set this to skip the
   * Suspense fallback. Default false.
   */
  readonly suppressSuspenseFallback?: boolean;
}

// ---------------------------------------------------------------------------
// Helpers — internal, presentational
// ---------------------------------------------------------------------------

function ChartFallback({ ariaLabel }: { ariaLabel: string }): React.JSX.Element {
  return (
    <output
      aria-label={ariaLabel}
      aria-busy
      className="flex h-full w-full items-center justify-center"
    >
      <Skeleton className="h-full w-full" />
    </output>
  );
}

function EmptyState(): React.JSX.Element {
  return (
    <output className="flex h-full w-full items-center justify-center text-[12px] text-ink-3">
      <Trans>Nessun dato</Trans>
    </output>
  );
}

/**
 * Real HTML `<table>` view of the chart data. Used for `type="table"` AND
 * as the screen-reader-accessible alternative toggled by the skip-link.
 *
 * Wire shape stays compatible with the eventual `<DataPagedTable>`
 * primitive (PR-A2): same column-order semantics, same `formatCell()`
 * fallback, same hairline-divided look.
 */
const ChartTable: React.FC<{
  data: ReadonlyArray<Record<string, unknown>>;
  columns: ReadonlyArray<string>;
  title?: string;
  columnMeta?: ReadonlyArray<DataColumnMeta>;
}> = ({ data, columns, title, columnMeta }) => {
  return (
    <div className="h-full w-full overflow-auto" aria-label={title}>
      <DataPagedTable data={data} columns={columns} pageSize={25} columnMeta={columnMeta} />
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export const Chart: React.FC<ChartProps> = ({
  type,
  data,
  columns,
  viz,
  title,
  className,
  onElementClick,
  ariaLabel,
  suppressSuspenseFallback,
}) => {
  const { i18n } = useI18n();
  const reducedMotion = useReducedMotion();
  const [tableView, setTableView] = React.useState(false);
  const [expandedRemainderKey, setExpandedRemainderKey] = React.useState<string | null>(null);

  const option = React.useMemo(
    () => buildOption(type, data, columns, viz, { reducedMotion }),
    [type, data, columns, viz, reducedMotion],
  );

  const pieRemainder = React.useMemo(() => {
    if (type !== "pie" || !option) return [];
    const series = Array.isArray(option.series) ? option.series : [option.series];
    for (const entry of series) {
      const items = (entry as { data?: unknown }).data;
      if (!Array.isArray(items)) continue;
      for (const item of items) {
        const children = (item as { children?: unknown }).children;
        if (Array.isArray(children)) {
          return children as ReadonlyArray<{ name: string; value: number }>;
        }
      }
    }
    return [];
  }, [option, type]);

  const remainderKey = React.useMemo(
    () =>
      pieRemainder.length > 0
        ? pieRemainder.map((item) => `${item.name}:${item.value}`).join("|")
        : null,
    [pieRemainder],
  );
  const remainderExpanded = remainderKey !== null && expandedRemainderKey === remainderKey;

  const onEvents = React.useMemo<Record<string, (params: unknown) => void> | undefined>(() => {
    if (!onElementClick && pieRemainder.length === 0) return undefined;
    return {
      click: (raw: unknown) => {
        const p = raw as
          | {
              name?: string;
              seriesName?: string;
              value?: unknown;
              componentType?: string;
              data?: { children?: unknown };
            }
          | undefined;
        if (!p?.name || p.componentType !== "series") return;
        if (Array.isArray(p.data?.children)) {
          if (remainderKey) setExpandedRemainderKey(remainderKey);
          return;
        }
        onElementClick?.({
          field: resolveChartDimension(data, columns, viz),
          name: p.name,
          seriesName: p.seriesName,
          value: Array.isArray(p.value)
            ? Number(p.value[1])
            : typeof p.value === "number"
              ? p.value
              : Number(p.value ?? 0),
        });
      },
    };
  }, [onElementClick, pieRemainder.length, remainderKey, data, columns, viz]);

  const resolvedLabel =
    ariaLabel ?? (title ? i18n._(msg`Grafico ${type}: ${title}`) : i18n._(msg`Grafico ${type}`));

  // Empty data — same message regardless of chart type.
  if (!data || data.length === 0) {
    return (
      <div
        aria-label={resolvedLabel}
        role="img"
        className={cn("relative h-full w-full", className)}
      >
        <EmptyState />
      </div>
    );
  }

  const isTable = type === "table" || tableView;

  return (
    <div
      aria-label={resolvedLabel}
      role="region"
      className={cn("relative flex h-full w-full flex-col", className)}
    >
      <div className="min-h-0 flex-1">
        {isTable || option === null ? (
          <ChartTable
            data={data}
            columns={resolveChartTableColumns(
              columns.length > 0 ? columns : Object.keys(data[0] ?? {}),
              viz,
            )}
            title={title}
            columnMeta={resolveChartColumnMeta(viz)}
          />
        ) : suppressSuspenseFallback ? (
          <React.Suspense fallback={null}>
            <ChartImplLazy option={option} onEvents={onEvents} />
          </React.Suspense>
        ) : (
          <React.Suspense fallback={<ChartFallback ariaLabel={resolvedLabel} />}>
            <ChartImplLazy option={option} onEvents={onEvents} />
          </React.Suspense>
        )}
      </div>
      {!isTable && pieRemainder.length > 0 ? (
        <div className="mt-1 shrink-0 rounded-md border border-hairline bg-cream/70">
          <button
            type="button"
            className="flex w-full items-center justify-between gap-2 px-2.5 py-1.5 text-left text-[11px] text-ink-2 transition-colors hover:bg-cream-deep focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/40"
            aria-expanded={remainderExpanded}
            onClick={() =>
              setExpandedRemainderKey((value) => (value === remainderKey ? null : remainderKey))
            }
          >
            <span>
              {i18n._(msg`Mostra ${pieRemainder.length} categorie raggruppate in “Altri”`)}
            </span>
            {remainderExpanded ? (
              <ChevronUp size={13} aria-hidden />
            ) : (
              <ChevronDown size={13} aria-hidden />
            )}
          </button>
          {remainderExpanded ? (
            <div className="max-h-[180px] overflow-auto border-hairline border-t p-1.5">
              <DataPagedTable
                data={pieRemainder.map((item) => ({
                  categoria: item.name,
                  valore: item.value,
                }))}
                columns={["categoria", "valore"]}
                pageSize={25}
              />
            </div>
          ) : null}
        </div>
      ) : null}
      {/* Accessibility skip-link — only meaningful for non-table charts. */}
      {type !== "table" ? (
        <button
          type="button"
          onClick={() => setTableView((p) => !p)}
          className={cn(
            "mt-1 inline-flex items-center gap-1 self-start rounded px-1.5 py-0.5 text-[10px] text-ink-3 uppercase tracking-[0.04em]",
            "transition-colors hover:bg-cream-deep hover:text-ink-1",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/40",
          )}
          aria-pressed={tableView}
        >
          <TableIcon size={10} strokeWidth={1.75} aria-hidden />
          {tableView ? i18n._(msg`Visualizza come grafico`) : i18n._(msg`Visualizza come tabella`)}
        </button>
      ) : null}
    </div>
  );
};
Chart.displayName = "Chart";
